var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import { DateTime } from 'luxon';
import { gql } from '@apollo/client';
import { ShiftOpportunityType } from '@amzn/atoz-shiftapp-types';
import { UnavailableShiftTranslationKeys } from '../../features/ShiftFilters/ShiftFilters/UnavailableShiftFilter/UnavailableShiftFilterTypes';
import { ShiftTypeTranslationKeys } from '../../features/ShiftFilters/ShiftFilters/ShiftTypeFilter/ShiftTypeFilterTypes';
import { HOURS_CAP_INELIGIBILITIES, OVERLAPS_WITH_SCHEDULE_INELIGIBILITIES } from '../../utils/shiftOpportunityUtils';
import { isTimeSameOrBefore } from '@amzn/atoz-shiftapp-utilities';
import { useDynamicTranslation } from '../../hooks';
function matchesLocationFilters(opportunity, locationFilters) {
    return locationFilters && !!locationFilters[opportunity.shift.site.id];
}
function matchesShiftTypeFilter(opportunity, shiftTypeFilter) {
    var isOnlyWithPremiumChecked = !!shiftTypeFilter[ShiftTypeTranslationKeys.ONLY_WITH_PREMIUM];
    if (isOnlyWithPremiumChecked) {
        return !!opportunity.incentivePay;
    }
    return true;
}
function isAddOpportunity(opportunity) {
    return (opportunity.type === ShiftOpportunityType.Add ||
        opportunity.type === ShiftOpportunityType.VoluntaryExtraTime);
}
function hasIneligibilityCode(unclaimableReasonCodes, codes) {
    for (var _i = 0, _a = unclaimableReasonCodes || []; _i < _a.length; _i++) {
        var ineligibleCode = _a[_i];
        if (codes.has(ineligibleCode)) {
            return true;
        }
    }
    return false;
}
function doesShiftOpportunityExceedHoursCap(opportunity) {
    var _a;
    return hasIneligibilityCode((_a = opportunity.eligibility) === null || _a === void 0 ? void 0 : _a.unclaimableReasonCodes, HOURS_CAP_INELIGIBILITIES);
}
function doesShiftOpportunityOverlapSchedule(opportunity) {
    var _a;
    return hasIneligibilityCode((_a = opportunity.eligibility) === null || _a === void 0 ? void 0 : _a.unclaimableReasonCodes, OVERLAPS_WITH_SCHEDULE_INELIGIBILITIES);
}
function matchesUnavailabilityFilter(opportunity, unavailableFilter) {
    var _a;
    var isEligible = !!opportunity.eligibility
        ? !!((_a = opportunity.eligibility) === null || _a === void 0 ? void 0 : _a.isEligible)
        : true;
    var doesExceedHoursCap = doesShiftOpportunityExceedHoursCap(opportunity);
    var doesOverlap = doesShiftOpportunityOverlapSchedule(opportunity);
    var showIneligible = !!unavailableFilter[UnavailableShiftTranslationKeys.SHOW_ALL_UNAVAILABLE];
    var showHoursCapped = !!unavailableFilter[UnavailableShiftTranslationKeys.EXCEEDS_HOURS_CAP];
    var showOverlapping = !!unavailableFilter[UnavailableShiftTranslationKeys.OVERLAPS_WITH_SCHEDULE];
    if (showIneligible && !isEligible) {
        return true;
    }
    if (showHoursCapped && doesExceedHoursCap) {
        return true;
    }
    if (showOverlapping && doesOverlap) {
        return true;
    }
    return isEligible;
}
function matchesShiftTimeFilter(opportunity, shiftTimeFilter) {
    var filterStartTime = shiftTimeFilter.startTime, filterEndTime = shiftTimeFilter.endTime;
    if (!filterStartTime && !filterEndTime) {
        return true;
    }
    var _a = opportunity.shift.timeRange, rawShiftStartTime = _a.start, rawShiftEndTime = _a.end;
    // @todo - this parsing should be done in a local-only field
    var shiftStartTime = DateTime.fromISO(rawShiftStartTime);
    var shiftEndTime = DateTime.fromISO(rawShiftEndTime);
    // if only start range is included then check that we are before
    if (!filterEndTime && isTimeSameOrBefore(filterStartTime, shiftStartTime)) {
        return true;
    }
    if (isTimeSameOrBefore(filterStartTime, filterEndTime)) {
        return matchesSameDayFilter(filterStartTime, filterEndTime, shiftStartTime, shiftEndTime);
    }
    else {
        return matchesNextDayFilter(filterStartTime, filterEndTime, shiftStartTime, shiftEndTime);
    }
}
function matchesSameDayFilter(filterStartTime, filterEndTime, shiftStartTime, shiftEndTime) {
    var isIncluded = isTimeSameOrBefore(filterStartTime, shiftStartTime) &&
        isTimeSameOrBefore(shiftEndTime, filterEndTime);
    var isShiftSameDay = isTimeSameOrBefore(shiftStartTime, shiftEndTime);
    return isIncluded && isShiftSameDay;
}
function matchesNextDayFilter(filterStartTime, filterEndTime, shiftStartTime, shiftEndTime) {
    var isSameDay = isTimeSameOrBefore(shiftStartTime, shiftEndTime);
    var isShiftEndCurrentDayAndIncluded = isTimeSameOrBefore(filterStartTime, shiftStartTime) &&
        isTimeSameOrBefore(filterEndTime, shiftEndTime) &&
        isSameDay;
    var isShiftEndNextDayAndIncluded = isTimeSameOrBefore(filterStartTime, shiftStartTime) &&
        isTimeSameOrBefore(shiftEndTime, filterEndTime) &&
        !isSameDay;
    return isShiftEndCurrentDayAndIncluded || isShiftEndNextDayAndIncluded;
}
export function useFilteredShifts(_a) {
    var shiftOpportunities = _a.shiftOpportunities, selectedDate = _a.selectedDate, filterState = _a.filterState, shouldCheckJobTypeFilter = _a.shouldCheckJobTypeFilter;
    var dt = useDynamicTranslation().dt;
    function matchesJobTypeFilter(opportunity, jobTypeFilter) {
        return opportunity.skills.some(function (skill) {
            return !!jobTypeFilter[dt(skill)];
        });
    }
    // filter the opportunities
    var filteredOpportunities = React.useMemo(function () {
        var filteredOpportunities = shiftOpportunities
            ? __spreadArray([], shiftOpportunities, true) : [];
        if (!filterState) {
            return filteredOpportunities;
        }
        return filteredOpportunities
            .sort(function (a, b) {
            var _a, _b;
            var isEligibleA = !!((_a = a.eligibility) === null || _a === void 0 ? void 0 : _a.isEligible);
            var isEligibleB = !!((_b = b.eligibility) === null || _b === void 0 ? void 0 : _b.isEligible);
            // If eligibility is the same, sort by start time
            if (isEligibleA === isEligibleB) {
                var startTimeA = DateTime.fromISO(a.shift.timeRange.start).toMillis();
                var startTimeB = DateTime.fromISO(b.shift.timeRange.start).toMillis();
                // sort ascending
                return startTimeA - startTimeB;
            }
            return Number(isEligibleB) - Number(isEligibleA);
        })
            .filter(function (shiftOpportunity) {
            return isAddOpportunity(shiftOpportunity) &&
                matchesLocationFilters(shiftOpportunity, filterState.location) &&
                matchesUnavailabilityFilter(shiftOpportunity, filterState.unavailable) &&
                matchesShiftTypeFilter(shiftOpportunity, filterState.shiftType) &&
                matchesShiftTimeFilter(shiftOpportunity, filterState.shiftTime) &&
                (!shouldCheckJobTypeFilter ||
                    matchesJobTypeFilter(shiftOpportunity, filterState.jobType));
        });
    }, [shiftOpportunities, filterState]);
    // Get the opportunities for the selected date
    var opportunitiesForDay = React.useMemo(function () {
        return filteredOpportunities.filter(function (_a) {
            var shift = _a.shift;
            return DateTime.fromISO(shift.timeRange.start).toISODate() ===
                selectedDate.toISODate();
        });
    }, [filteredOpportunities, selectedDate]);
    return opportunitiesForDay;
}
export var UseFilteredShiftsFragment = gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    fragment FilteredShifts_ShiftOpportunity on ShiftOpportunity {\n        type\n        shift {\n            site {\n                id\n            }\n            timeRange {\n                start\n                end\n            }\n        }\n        eligibility {\n            isEligible\n            unclaimableReasonCodes\n        }\n        incentivePay {\n            netPayModifier {\n                code\n            }\n            payModifiers {\n                name\n            }\n        }\n        skill\n        skills {\n            translationKey\n            namespace\n            defaultText\n        }\n    }\n"], ["\n    fragment FilteredShifts_ShiftOpportunity on ShiftOpportunity {\n        type\n        shift {\n            site {\n                id\n            }\n            timeRange {\n                start\n                end\n            }\n        }\n        eligibility {\n            isEligible\n            unclaimableReasonCodes\n        }\n        incentivePay {\n            netPayModifier {\n                code\n            }\n            payModifiers {\n                name\n            }\n        }\n        skill\n        skills {\n            translationKey\n            namespace\n            defaultText\n        }\n    }\n"])));
var templateObject_1;
